// FLAGS: none
// Local form primitives (Select and friends) styled to match the shared UI kit.
import * as React from "react";

/**
 * Local Select for the Spaces UI. Buttons/Badge/Card/Input/Textarea/Table now
 * come from @openup/shared/primitives/ui. Select stays a native control here
 * because the shared select primitive is Radix (@radix-ui/react-select) — a
 * composed popover API this app hasn't adopted — so we keep a native <select>
 * styled to match the shared Input.
 */

/** Matches @openup/shared Input styling so native controls look native. */
export const fieldClass =
  "flex w-full text-neutral-900 rounded-lg border-2 border-neutral-300 bg-white px-4 py-3 text-sm focus:border-green-500 outline-none transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50";

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  invalid?: boolean;
};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className = "", invalid = false, ...props }, ref) => (
    <select
      ref={ref}
      aria-invalid={invalid}
      className={`${fieldClass} ${invalid ? "border-red-500" : ""} ${className}`}
      // Deliberate prop spread, and the case frontend/no-prop-spreading's primitive exception
      // describes: a thin wrapper over a native control whose whole purpose is forwarding arbitrary
      // select attributes (name, value, onChange, required, disabled...). Enumerating them would be
      // maintenance with no safety gain. The file sits under @src rather than @openup/shared only
      // because it is Spaces-local for now.
      {...props}
    />
  ),
);
Select.displayName = "Select";
